import type { ReactNode } from 'react'
import Icon from '../icons'

export function Panel({
  title,
  sub,
  actions,
  children,
  bodyClass,
}: {
  title?: ReactNode
  sub?: ReactNode
  actions?: ReactNode
  children: ReactNode
  bodyClass?: string
}) {
  return (
    <section className="panel">
      {title ? (
        <header className="panel-head">
          <h2>{title}</h2>
          {sub ? <span className="sub">{sub}</span> : null}
          <div className="spacer" />
          {actions}
        </header>
      ) : null}
      <div className={bodyClass ?? 'panel-body'}>{children}</div>
    </section>
  )
}

export type Tone = 'blue' | 'green' | 'orange' | 'red' | 'violet' | 'grey'

export function Badge({ tone = 'grey', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge b-${tone}`}>{children}</span>
}

const STATUS_TONE: Record<string, Tone> = {
  effective: 'green', published: 'green', succeeded: 'green', approved: 'green', frozen: 'green', done: 'green',
  approved_partial: 'green', closed: 'green', deleted: 'green',
  draft: 'grey', queued: 'blue', running: 'blue', awaiting_approval: 'orange', pending: 'orange', pending_deletion: 'orange', in_review: 'orange',
  changes_requested: 'orange', withdrawn: 'orange', superseded: 'grey', quarantined: 'red', failed: 'red',
  dead_letter: 'red', rejected: 'red', returned: 'orange', cancelled: 'grey', expired: 'red', restoring: 'blue',
}

/** 面向业务人员的状态文字；原始枚举只保留在 title 中，便于问题定位。 */
const STATUS_LABEL: Record<string, string> = {
  effective: '已生效', published: '已发布', succeeded: '已完成', approved: '已通过', approved_partial: '部分通过', closed: '已完成', deleted: '已归档',
  draft: '草稿', queued: '等待处理', running: '处理中', awaiting_approval: '等待确认', pending: '待处理', pending_deletion: '等待删除确认', in_review: '审核中',
  changes_requested: '需要修改', returned: '已退回', rejected: '已驳回', withdrawn: '已撤回', superseded: '已替代', quarantined: '已隔离',
  failed: '处理失败', dead_letter: '处理失败', cancelled: '已取消', expired: '已失效', restoring: '恢复中', abandoned: '已结束', archived: '已归档',
}

export function StatusBadge({ status }: { status: string }) {
  return <span title={`系统状态：${status}`}><Badge tone={STATUS_TONE[status] ?? 'grey'}>{STATUS_LABEL[status] ?? status}</Badge></span>
}

export function Note({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger' | 'ok'; children: ReactNode }) {
  return <div className={`note ${tone}`}>{children}</div>
}

export function State({
  icon = 'alert',
  big,
  children,
  action,
}: {
  icon?: string
  big: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="state">
      <Icon name={icon} size={26} />
      <div className="big">{big}</div>
      {children ? <div>{children}</div> : null}
      {action}
    </div>
  )
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="list" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skel" style={{ width: `${92 - i * 9}%` }} />)}
    </div>
  )
}

export function Tabs({
  items,
  value,
  onChange,
}: {
  items: Array<{ key: string; label: ReactNode }>
  value: string
  onChange: (key: string) => void
}) {
  return (
    <div className="tabs" role="tablist">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={value === item.key}
          className={`tab${value === item.key ? ' is-active' : ''}`}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

export function Flow({ steps, done = [] }: { steps: string[]; done?: string[] }) {
  return (
    <div className="flow">
      {steps.map((step, index) => (
        <span key={step} style={{ display: 'contents' }}>
          {index > 0 ? <span className="arrow">→</span> : null}
          <span className={`step${done.includes(step) ? ' done' : ''}`}>{step}</span>
        </span>
      ))}
    </div>
  )
}

export function Metric({ k, v, hint }: { k: string; v: ReactNode; hint?: ReactNode }) {
  return (
    <div className="metric">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      {hint ? <div className="s muted">{hint}</div> : null}
    </div>
  )
}

/** 无权限时不是隐藏入口而已：原型要求给出「为什么不可用 + 如何申请」。 */
export function DeniedNote({ permission, roles, onRequest }: { permission: string; roles: string[]; onRequest?: () => void }) {
  return (
    <div className="perm-note">
      <Icon name="shield" size={14} />
      <span>
        当前身份缺少 <b className="mono">{permission}</b>，操作不可用（角色：{roles.join(' / ') || '—'}）。
      </span>
      {onRequest ? (
        <button type="button" className="linkish" onClick={onRequest}>申请权限</button>
      ) : null}
    </div>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="s muted">{hint}</span> : null}
    </label>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted" style={{ margin: 0 }}>{children}</p>
}
